<template>
  <div class="act-list-container mt-4">
    <div class="act-list-header">
      <h4 class="mb-0">
        {{
          $t("toolbar.acts.actsList", {
            entity: $getVisibleNames(
              "manual.acta_de_comite",
              true,
              "Actas de Comités"
            ),
          })
        }}
      </h4>
    </div>
    <b-table
      :items="orderedActs"
      :fields="fields"
      show-empty
      :empty-text="$t('toolbar.acts.noActsToShow')"
      thead-class="act-list-thead"
      tbody-class="act-list-tbody"
    >
      <template #cell(start_date)="data">
        <span v-if="is_institutional">{{ data.value | FormatToDate }}</span>
        <span v-else>{{ data.value | FormatToDateTime }}</span>
      </template>
      <template #cell(end_date)="data">
        {{ data.value | FormatToDateTime }}
      </template>
      <template #cell(agreement)="data">
        <span
          v-b-tooltip.v-secondary.noninteractive="
            $t('toolbar.acts.fulfilledAgreements')
          "
        >
          {{ data.item.agreements_fullfiled.length }}
        </span>
        /
        <span
          v-b-tooltip.v-secondary.noninteractive="
            $t('toolbar.acts.totalAgreements')
          "
        >
          {{ data.item.agreements.length }}
        </span>
      </template>
      <template #cell(attachments)="data">
        <template v-if="data.item.files && data.item.files.length > 0">
          <b-button
            :id="`popover-files-${data.item.id}`"
            variant="none"
            size="sm"
            class="btn-attachments"
            v-b-tooltip.v-secondary.noninteractive="
              $t('toolbar.acts.attachments')
            "
            @click="fetchActFiles(data.item)"
          >
            <b-icon icon="paperclip"></b-icon>
            <b-badge variant="secondary" pill class="ml-1">{{
              data.item.files.length
            }}</b-badge>
          </b-button>
          <b-popover
            :target="`popover-files-${data.item.id}`"
            triggers="click blur"
            placement="left"
            custom-class="act-files-popover"
          >
            <template #title>
              <b-icon icon="paperclip" class="mr-1"></b-icon>
              {{ $t("toolbar.acts.attachments") }}
            </template>
            <div v-if="loadingFiles[data.item.id]" class="text-center py-2">
              <b-spinner small variant="secondary"></b-spinner>
            </div>
            <div
              v-else-if="
                actFiles[data.item.id] && actFiles[data.item.id].length > 0
              "
              class="popover-file-list"
            >
              <div
                v-for="file in actFiles[data.item.id]"
                :key="file.id"
                class="popover-file-item"
              >
                <b-icon
                  icon="file-earmark-text"
                  class="mr-2 text-secondary"
                ></b-icon>
                <a
                  href="#"
                  class="popover-file-link"
                  @click.prevent="downloadFile(file.id)"
                >
                  {{ getFileName(file) }}
                </a>
              </div>
            </div>
            <div v-else class="text-muted small py-1">
              {{ $t("toolbar.acts.noAttachments") }}
            </div>
          </b-popover>
        </template>
        <span v-else class="text-muted">—</span>
      </template>
      <template #cell(actions)="data">
        <ButtonSmall
          v-can="'cideu.add_act'"
          :tooltip_text="
            $t('reusable.common.viewTooltip', {
              entity: $getVisibleNames(
                'manual.acta_de_comite',
                false,
                'Acta de Comité'
              ),
            })
          "
          :text_button="``"
          :click_button="() => emitOpenAct(data.item)"
          :icon="'eye'"
          :variant="'none'"
          :custom_class="``"
        >
        </ButtonSmall>
        <ButtonSmall
          v-if="
            user &&
            (data.item.created_by == user.email ||
              data.item.can_edit_user_list.includes(user.id))
          "
          :tooltip_text="``"
          :click_button="() => askForDeleteAct(data.item.id)"
          :icon="'trash'"
          :variant="'none'"
          :custom_class="`ml-2`"
        >
        </ButtonSmall>
      </template>
    </b-table>
  </div>
</template>

<script>
import { toast, downloadFileByUrl } from "@/utils/utils";
import { mapGetters } from "vuex";
import * as names from "@/store/names";

export default {
  name: "ActList",
  components: {
    ButtonSmall: () =>
      import("@/components/reusable/Buttons/Small/ButtonSmall"),
  },
  props: {
    Acts: {
      type: Array,
    },
    allows_crud: {
      type: Boolean,
      default: false,
    },
    is_institutional: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      actFiles: {},
      loadingFiles: {},
    };
  },
  computed: {
    ...mapGetters({
      user: "getUser",
    }),
    fields() {
      const all = [
        {
          key: "number",
          label: this.$t("toolbar.acts.number"),
          sortable: true,
          thClass: "th-class",
        },
        {
          key: "start_date",
          label: this.is_institutional
            ? this.$t("reusable.common.date")
            : this.$t("reusable.common.startDate"),
          thClass: "th-class",
          sortable: true,
        },
        !this.is_institutional && {
          key: "end_date",
          label: this.$t("reusable.common.endDate"),
          thClass: "th-class",
          sortable: true,
        },
        !this.is_institutional && {
          key: "agreement",
          label: this.$t("toolbar.acts.agreements"),
          thClass: "th-class",
          sortable: false,
          headerTitle: this.$t("toolbar.acts.fulfilledTotals"),
        },
        {
          key: "attachments",
          label: this.$t("toolbar.acts.attachments"),
          thClass: "th-class",
          sortable: false,
        },
        {
          key: "actions",
          thClass: "th-class",
          label: "",
        },
      ];
      return all.filter(Boolean);
    },
    orderedActs() {
      let list = [];
      let index = 1;
      this.Acts.forEach((act) => {
        act.number = index;
        list.push(act);
        index += 1;
      });
      return list;
    },
  },
  methods: {
    fetchActFiles(act) {
      // Si los archivos ya están en caché y el conteo coincide con act.files, no volver a solicitarlos
      // Si difieren (se agregó o eliminó un archivo), se invalida el caché y se re-fetch
      const cached = this.actFiles[act.id];
      if (cached && cached.length === act.files.length) return;
      // Activar el spinner de carga para esta acta
      this.$set(this.loadingFiles, act.id, true);
      // Crear una promesa por cada ID de archivo para obtener su detalle desde la API
      const promises = act.files.map((fileId) =>
        this.$restful.Get(`/common/file/${fileId}/`)
      );
      // Esperar a que todas las solicitudes finalicen y guardar los resultados
      Promise.all(promises).then((files) => {
        this.$set(this.actFiles, act.id, files);
        // Desactivar el spinner una vez cargados todos los archivos
        this.$set(this.loadingFiles, act.id, false);
      });
    },
    getFileName(file) {
      // "name" trae el nombre original; la ruta lleva prefijo usuario_fecha y sufijo aleatorio.
      if (file.name) return file.name;
      if (!file.file) return "";
      const parts = file.file.split("?")[0].split("/");
      return decodeURIComponent(parts[parts.length - 1]);
    },
    downloadFile(file_id) {
      this.$store.dispatch(names.FETCH_FILE, file_id).then((file) => {
        if (file) {
          if (file.presigned_url != null) {
            downloadFileByUrl(file.presigned_url);
          } else if (file.file != null) {
            downloadFileByUrl(file.file);
          }
        }
      });
    },
    emitOpenAct(act) {
      this.$emit("open", act.id);
    },
    askForDeleteAct(act_id) {
      this.$swal({
        title: this.$t("toolbar.acts.deleteActConfirmation"),
        text: this.$t("reusable.common.deleteConfirmText"),
        type: "warning",
        showCancelButton: true,
      }).then((result) => {
        if (result.value) {
          this.deleteAct(act_id);
        }
      });
    },
    deleteAct(act_id) {
      this.$restful.Delete(`/cideu/act/${act_id}/`).then(() => {
        this.$emit("deleted", act_id);
        toast(this.$t("toolbar.acts.actDeletedSuccessfully"));
      });
    },
  },
};
</script>

<style scoped>
.act-list-container {
  background: var(--kl-surface);
  /* border: 1px solid var(--kl-table-border); */
  border-radius: var(--kl-table-radius);
  overflow: hidden;
}

.act-list-header {
  padding: var(--kl-space-4) var(--kl-space-5);
  text-align: center;
}

.act-list-header h4 {
  font-weight: var(--kl-fw-bold);
  color: var(--kl-text-heading);
}

.act-list-container >>> .act-list-thead th {
  background: var(--kl-color-accent-medium) !important;
  color: var(--kl-text-on-primary) !important;
  font-weight: var(--kl-fw-bold);
  text-align: center;
  vertical-align: middle;
  padding: var(--kl-space-3);
}

.act-list-container >>> .act-list-tbody td {
  background: var(--kl-table-row-bg);
  color: var(--kl-table-text);
  font-size: var(--kl-table-fs);
  padding: var(--kl-space-3);
  vertical-align: middle;
  text-align: center;
  border-color: var(--kl-table-border);
}

.act-list-container >>> .act-list-tbody tr:nth-child(even) td {
  background: var(--kl-table-row-alt-bg);
}

.act-list-container >>> .act-list-tbody tr:hover td {
  background: var(--kl-table-row-hover-bg);
}

.act-list-container >>> table {
  margin-bottom: 0;
}

.btn-attachments {
  padding: 0.2rem 0.4rem;
  line-height: 1;
}

.btn-attachments:focus {
  box-shadow: none;
}

.act-list-container >>> .act-files-popover {
  min-width: 220px;
  max-width: 320px;
}

.popover-file-list {
  max-height: 200px;
  overflow-y: auto;
}

.popover-file-item {
  display: flex;
  align-items: center;
  padding: 0.3rem 0;
  border-bottom: 1px solid var(--kl-border-soft);
}

.popover-file-item:last-child {
  border-bottom: none;
}

.popover-file-link {
  font-size: var(--kl-fs-small);
  color: var(--kl-text-link);
  word-break: break-all;
  text-decoration: none;
}

.popover-file-link:hover {
  color: var(--kl-color-primary-hover);
  text-decoration: underline;
}
</style>